import React, { useCallback, useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";

import TextInput from "../../../Inputs/TextInput";
import ReadOnlyProtectedForm from "../../../Inputs/ReadOnlyProtectedForm";
import { useTranslation } from "react-i18next";
import Gate from "../../../../interfaces/robot/rooms/gate";
import {
  useSharedActions,
  useSharedDispatch,
  useSharedSelector,
} from "../../../../store/SharedProvider";
import useServerValidationResponseEffect from "../../../../hooks/useServerValidationResponseEffect";
import { selectGatesRestSet } from "../../../../store/room/zone-planner/zone-planner-selectors";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";
import useValidatedInputEffect from "../../../../hooks/useValidatedInputEffect";
import { validateGateName } from "../../../../validation/gate-validation";
import { selectSelectedRoomGateIdsIdentifiersAndNames } from "../../../../store/room/room-selectors";
import SelectedGateFormApiSelection from "./SelectedGateFormApiSelection";
import { selectIsWebApp } from "../../../../store/app-configurations/app-configurations-selectors";
import {
  selectRobotApiConfigs,
  selectRobotApiConfigsRestSet,
} from "../../../../store/robot-configurations/robot-configurations-selectors";
import { getRobotApiConfig } from "../../../../interfaces/robot/robot-apis/robot-api-config";
import ApiEndpointNames from "../../../../enums/robot/configuration/api/api-endpoint-names";
import ApiProtocol from "../../../../enums/robot/configuration/api/api-protocol";

const SelectedGateForm: React.FC<{
  gate: Gate;
}> = ({ gate }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const nonSelectedGateIdsIdentifiersNames = useSharedSelector((state) =>
    selectSelectedRoomGateIdsIdentifiersAndNames(state),
  ).filter((g) => g.id !== gate.id);
  const robotApiConfigs = useSharedSelector((state) => selectRobotApiConfigs(state));
  const robotApiConfigsRest = useSharedSelector((state) => selectRobotApiConfigsRestSet(state));
  const gatesRestSet = useSharedSelector((state) => selectGatesRestSet(state));
  const isWebapp = useSharedSelector((state) => selectIsWebApp(state));

  const {
    patchGatesRestSet,
    patchSelectedGate,
    patchGateById,
    getRobotApiConfigs,
  } = useSharedActions();

  useEffect(() => {
    if (robotApiConfigsRest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getRobotApiConfigs());
    }
  }, [dispatch, robotApiConfigsRest.getStatus]);

  const { resetToggle, removeServerError, getServerValidationResultOrNull } =
    useServerValidationResponseEffect({
      patchError: gatesRestSet.patchError,
      resetPatchHandler: () =>
        dispatch(
          patchGatesRestSet({
            patchStatus: HttpFetchStatus.Idle,
            patchError: null,
          }),
        ),
    });
  const {
    value: gateName,
    validationResult: gateNameValidationResult,
    valueChangeHandler: gateNameChangeHandler,
  } = useValidatedInputEffect(
    gate.name ?? "",
    (newName) =>
      validateGateName(
        newName,
        nonSelectedGateIdsIdentifiersNames
          .filter((z) => z.name != null)
          .map((z) => z.name!),
      ),
      (_) => {},
    getServerValidationResultOrNull("name"),
    removeServerError,
    resetToggle,
  );

  const patchGateHandler = () => {
    if (gate.name !== gateName && gateNameValidationResult.isValid) { 
      dispatch(patchGateById({ id: gate.id, data: { gate: { name: gateName } } }));
    }
  };

  const patchGateHandlerRef = useRef(patchGateHandler);
  patchGateHandlerRef.current = patchGateHandler; 

  useEffect(() => {
    return () => {
      patchGateHandlerRef.current();
    };
  }, []);

  const formSubmitHandler = (event: React.FormEvent) => {
    event.preventDefault();
    patchGateHandler();
  };

  return (
    <React.Fragment>
      <ReadOnlyProtectedForm
        noValidate
        autoComplete="off"
        onSubmit={formSubmitHandler}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInput
              label={t("label-gate-name")}
              onBlur={patchGateHandler}
              onChange={gateNameChangeHandler}
              value={gateName}
              validationResult={gateNameValidationResult}
            />
          </Grid>
          {isWebapp && (
            (getRobotApiConfig(ApiEndpointNames.GATES, robotApiConfigs)?.apiProtocol === ApiProtocol.Relay) &&
            <SelectedGateFormApiSelection
              gateId={gate.id}
              gateIdentifier={gate.identifier}
            />
          )}
        </Grid>
      </ReadOnlyProtectedForm>
    </React.Fragment>
  );
};

export default SelectedGateForm;
